import * as React from "react";
import { cn } from "@/lib/utils";

interface CircularProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
  progress: number; // 0-100
  size?: number; // diameter of the circle, corresponds to tailwind's size-xx
  children?: React.ReactNode;
}

export const CircularProgressBar: React.FC<CircularProgressBarProps> = ({
  progress,
  size = 40, // Default to size-40 (160px)
  className,
  children,
  ...props
}) => {
  const radius = 16; // Fixed radius based on viewBox 36 and stroke-width 2
  const strokeWidth = 2; // Fixed stroke-width based on example
  const circumference = 100; // Approximation for r=16 in a 0-100 dasharray context
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div
      className={cn(`relative size-${size} flex items-center justify-center`, className)}
      {...props}
    >
      <svg className="size-full -rotate-90" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
        {/* Background Circle */}
        <circle
          cx="18"
          cy="18"
          r={radius}
          fill="none"
          className="stroke-current text-neutral-700"
          strokeWidth={strokeWidth}
        ></circle>
        {/* Progress Circle */}
        <circle
          cx="18"
          cy="18"
          r={radius}
          fill="none"
          className="stroke-current text-primary-300 transition-all duration-300 ease-in-out"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        ></circle>
      </svg>

      {/* Center Content */}
      <div className="absolute inset-0 flex items-center justify-center">
        {children ?? (
          <span className="text-center text-sm font-bold text-primary-300">{`${Math.round(progress)}%`}</span>
        )}
      </div>
    </div>
  );
};
